import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOptionalOrganization } from '../../contexts/OrganizationContext';

// PHA-509: in-app browser tabs replacing the PatientDetail modal. Each open
// patient is one tab, dedup'd by patient_id; the leftmost tab ("patients" =
// the Patient List) is always present and non-closable.

// Persisted across page refresh so the user's open patient set survives a
// reload. Cleared on logout (see AuthContext.performLogout).
const STORAGE_KEY_TABS = 'phanova_patient_tabs_v1';
const STORAGE_KEY_ACTIVE = 'phanova_patient_tabs_active_v1';

function loadStoredTabs(): PatientTab[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_TABS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is PatientTab =>
        t &&
        typeof t === 'object' &&
        typeof (t as PatientTab).patientId === 'string' &&
        typeof (t as PatientTab).openNonce === 'number',
    );
  } catch {
    return [];
  }
}

function loadStoredActiveTabId(fallback: DashboardTabId): DashboardTabId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_ACTIVE);
    if (!raw) return fallback;
    return raw;
  } catch {
    return fallback;
  }
}

export function clearPatientTabsStorage(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY_TABS);
    window.localStorage.removeItem(STORAGE_KEY_ACTIVE);
  } catch {}
}

export type DashboardTabId = 'patients' | 'onboarding' | string;

export interface PatientTab {
  patientId: string;
  // Snapshot taken at open time so we can render the tab label without
  // refetching. The tab body still fetches fresh details via patientApi.get.
  firstName?: string;
  lastName?: string;
  mrn?: string | null;
  // Inner-tab hint forwarded to PatientDetail. Re-set on each openOrFocus call
  // so AckDrawer's post-ack chart link can land on the right inner tab even
  // when the patient tab is already open.
  initialTab?: number;
  // Bumps each time openOrFocus is called for this patient — PatientDetail
  // listens via key/effect so a re-open with a fresh `initialTab` re-syncs
  // the inner tab even when the same patient_id is already the active tab.
  openNonce: number;
  // PHA-1166: the clinic selected when the tab was opened. A patient only loads
  // under the clinic it belongs to, so tabs from any other clinic are closed as
  // soon as the selected clinic changes.
  orgId?: string;
}

export interface OpenOrFocusInput {
  patientId: string;
  firstName?: string;
  lastName?: string;
  mrn?: string | null;
  initialTab?: number;
}

interface PatientTabsContextValue {
  tabs: PatientTab[];
  activeTabId: DashboardTabId;
  setActiveTabId: (id: DashboardTabId) => void;
  openOrFocus: (input: OpenOrFocusInput) => void;
  close: (patientId: string) => void;
  clearAll: () => void;
}

const PatientTabsContext = createContext<PatientTabsContextValue | null>(null);

interface PatientTabsProviderProps {
  children: React.ReactNode;
  initialActiveTabId?: DashboardTabId;
  onActiveTabChange?: (id: DashboardTabId) => void;
  // Test escape hatch: skip the localStorage hydrate/persist round-trip so
  // unit tests don't bleed state into one another.
  disablePersistence?: boolean;
}

export const PatientTabsProvider: React.FC<PatientTabsProviderProps> = ({
  children,
  initialActiveTabId = 'patients',
  onActiveTabChange,
  disablePersistence = false,
}) => {
  const [tabs, setTabs] = useState<PatientTab[]>(() =>
    disablePersistence ? [] : loadStoredTabs(),
  );
  const [activeTabId, setActiveTabIdState] = useState<DashboardTabId>(() =>
    disablePersistence
      ? initialActiveTabId
      : loadStoredActiveTabId(initialActiveTabId),
  );
  const onActiveRef = useRef(onActiveTabChange);
  onActiveRef.current = onActiveTabChange;

  // PHA-1166: this provider sits above <Routes>, so it sees every clinic switch —
  // from the Dashboard header, the Billing report, or the Admin / Clinic layouts.
  // The old Dashboard-only effect missed the latter three (Dashboard was unmounted,
  // and remounted with the new clinic as its baseline), leaving tabs that answered
  // "Patient not found". `null` = no clinic resolved yet, or no OrganizationProvider.
  const organization = useOptionalOrganization();
  const currentOrgId =
    organization && !organization.loading && organization.orgId
      ? organization.orgId
      : null;
  const currentOrgRef = useRef(currentOrgId);
  currentOrgRef.current = currentOrgId;

  const lastOrgRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentOrgId === null) return;
    const previousOrgId = lastOrgRef.current;
    lastOrgRef.current = currentOrgId;
    // A real switch (not the first resolve after a page load) also leaves the
    // Onboarding tab, as the old Dashboard effect did.
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {
      setActiveTabIdState('patients');
    }
    // Tabs restored from storage without an orgId predate PHA-1166; their clinic is
    // unknown, so they are closed too.
    const stale = tabs.filter((t) => t.orgId !== currentOrgId);
    if (stale.length === 0) return;
    const staleIds = new Set(stale.map((t) => t.patientId));
    setTabs((prev) => prev.filter((t) => !staleIds.has(t.patientId)));
    setActiveTabIdState((current) => (staleIds.has(current) ? 'patients' : current));
    // Only a clinic change should close tabs — `tabs` is read, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  useEffect(() => {
    if (disablePersistence) return;
    try {
      window.localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(tabs));
    } catch {}
  }, [tabs, disablePersistence]);

  useEffect(() => {
    if (disablePersistence) return;
    try {
      window.localStorage.setItem(STORAGE_KEY_ACTIVE, activeTabId);
    } catch {}
  }, [activeTabId, disablePersistence]);

  const setActiveTabId = useCallback((id: DashboardTabId) => {
    setActiveTabIdState(id);
    onActiveRef.current?.(id);
  }, []);

  const openOrFocus = useCallback(
    (input: OpenOrFocusInput) => {
      const { patientId, firstName, lastName, mrn, initialTab } = input;
      setTabs((prev) => {
        const existing = prev.find((t) => t.patientId === patientId);
        if (existing) {
          // Always overwrite initialTab with the caller's value (even
          // undefined). A stale initialTab here would yank the user back to
          // a previously-routed inner tab on re-clicks or post-save refreshes.
          // Only bump openNonce when initialTab was explicitly supplied so
          // non-routing refreshes (rename / MRN snapshot) don't trigger the
          // PatientDetail re-sync effect.
          return prev.map((t) =>
            t.patientId === patientId
              ? {
                  ...t,
                  firstName: firstName ?? t.firstName,
                  lastName: lastName ?? t.lastName,
                  mrn: mrn !== undefined ? mrn : t.mrn,
                  initialTab,
                  openNonce:
                    initialTab !== undefined ? t.openNonce + 1 : t.openNonce,
                }
              : t,
          );
        }
        return [
          ...prev,
          {
            patientId,
            firstName,
            lastName,
            mrn: mrn ?? null,
            initialTab,
            openNonce: 1,
            orgId: currentOrgRef.current ?? undefined,
          },
        ];
      });
      setActiveTabId(patientId);
    },
    [setActiveTabId],
  );

  const clearAll = useCallback(() => {
    setTabs([]);
    setActiveTabIdState('patients');
  }, []);

  const close = useCallback(
    (patientId: string) => {
      setTabs((prev) => {
        const idx = prev.findIndex((t) => t.patientId === patientId);
        if (idx === -1) return prev;
        const next = prev.filter((t) => t.patientId !== patientId);
        // If we closed the active tab, fall back to the previous patient tab,
        // or the Patient List if this was the only one (or leftmost).
        setActiveTabIdState((current) => {
          if (current !== patientId) return current;
          if (next.length === 0) return 'patients';
          // Previous tab: idx - 1 if it exists, else idx (now the new tab at
          // that position). Both are valid "previous in strip" semantics.
          const fallback = next[Math.max(0, idx - 1)];
          return fallback ? fallback.patientId : 'patients';
        });
        return next;
      });
    },
    [],
  );

  const value = useMemo<PatientTabsContextValue>(
    () => ({ tabs, activeTabId, setActiveTabId, openOrFocus, close, clearAll }),
    [tabs, activeTabId, setActiveTabId, openOrFocus, close, clearAll],
  );

  return (
    <PatientTabsContext.Provider value={value}>
      {children}
    </PatientTabsContext.Provider>
  );
};

export function usePatientTabs(): PatientTabsContextValue {
  const ctx = useContext(PatientTabsContext);
  if (!ctx) {
    throw new Error(
      'usePatientTabs must be used inside a <PatientTabsProvider>',
    );
  }
  return ctx;
}

// PHA-509: optional accessor for components rendered above the provider that
// only want to dispatch the `phanova:open-patient-detail` CustomEvent. They
// should *not* assume the provider exists and should fall back to the event.
export function useOptionalPatientTabs(): PatientTabsContextValue | null {
  return useContext(PatientTabsContext);
}
